import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isObjectNotEmpty, isStringNotEmpty, isStringNotEmptyOrZeroLength } from '../utils/objectUtils';
import { create, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField, updateFields } from '../utils/dbUtils';
import { db, storage } from '../firebase';
import { toE164PhoneNumber } from '../utils/stringFormatter';
import { toLower } from 'lodash';
import { ref, uploadBytes } from 'firebase/storage';
import { v4 as uuid } from 'uuid';

class CoveredEntity {
	static collectionName = 'coveredEntities';

	name = null;
	coveredEntityType = null;

	slug = null;

	imageRefId = null;
	#image = null;

	providerFirstName = null;
	providerLastName = null;
	providerNPI = null;

	locationOfService = null;
	practiceName = null;
	practiceEIN = null;
	practiceNPI = null;

	legalName = null; // legal business name

	msid = null;

	billingAddress = null;
	shippingAddress = null;

	phone = null;
	fax = null; // new
	email = null; // new

	privacyPolicy = null;
	consentDocument = null;

	storeId = null;
	refId = null;

	constructor(coveredEntity) {
		if (isObjectNotEmpty(coveredEntity)) {
			if (isStringNotEmpty(coveredEntity.name)) { this.name = coveredEntity.name; }
			if (isStringNotEmpty(coveredEntity.coveredEntityType)) { this.coveredEntityType = coveredEntity.coveredEntityType; }

			if (isStringNotEmpty(coveredEntity.slug)) { this.slug = toLower(coveredEntity.slug); }

			if (isStringNotEmpty(coveredEntity.imageRefId)) { this.imageRefId = coveredEntity.imageRefId; }
			if (isNotNullOrUndefined(coveredEntity.image)) { this.#image = coveredEntity.image; }

			if (isStringNotEmpty(coveredEntity.providerFirstName)) { this.providerFirstName = coveredEntity.providerFirstName; }
			if (isStringNotEmpty(coveredEntity.providerLastName)) { this.providerLastName = coveredEntity.providerLastName; }
			if (isStringNotEmpty(coveredEntity.providerNPI)) { this.providerNPI = coveredEntity.providerNPI; }

			if (isStringNotEmpty(coveredEntity.locationOfService)) { this.locationOfService = coveredEntity.locationOfService; }
			if (isStringNotEmpty(coveredEntity.practiceName)) { this.practiceName = coveredEntity.practiceName; }
			if (isStringNotEmpty(coveredEntity.practiceEIN)) { this.practiceEIN = coveredEntity.practiceEIN; }
			if (isStringNotEmpty(coveredEntity.practiceNPI)) { this.practiceNPI = coveredEntity.practiceNPI; }

			if (isStringNotEmpty(coveredEntity.legalName)) { this.legalName = coveredEntity.legalName; }

			if (isStringNotEmpty(coveredEntity.msid)) { this.msid = coveredEntity.msid; }

			if (isObjectNotEmpty(coveredEntity.billingAddress)) { this.billingAddress = coveredEntity.billingAddress; }
			if (isObjectNotEmpty(coveredEntity.shippingAddress)) { this.shippingAddress = coveredEntity.shippingAddress; }

			if (isStringNotEmpty(coveredEntity.phone)) { this.phone = toE164PhoneNumber(coveredEntity.phone); }
			if (isStringNotEmpty(coveredEntity.fax)) { this.fax = toE164PhoneNumber(coveredEntity.fax); }
			if (isStringNotEmpty(coveredEntity.email)) { this.email = toLower(coveredEntity.email); }

			if (isStringNotEmpty(coveredEntity.privacyPolicy)) { this.privacyPolicy = coveredEntity.privacyPolicy; }
			if (isStringNotEmpty(coveredEntity.consentDocument)) { this.consentDocument = coveredEntity.consentDocument; }

			if (isStringNotEmpty(coveredEntity.storeId)) { this.storeId = coveredEntity.storeId; }
			if (isStringNotEmpty(coveredEntity.refId)) { this.refId = coveredEntity.refId; }
		}
	}

	toDto = () => {
		return {
			name: this.name,
			coveredEntityType: this.coveredEntityType,

			slug: this.slug,

			imageRefId: this.imageRefId,

			providerFirstName: this.providerFirstName,
			providerLastName: this.providerLastName,
			providerNPI: this.providerNPI,

			locationOfService: this.locationOfService,
			practiceName: this.practiceName,
			practiceEIN: this.practiceEIN,
			practiceNPI: this.practiceNPI,

			legalName: this.legalName,

			msid: this.msid,

			shippingAddress: this.shippingAddress,
			billingAddress: this.billingAddress,

			phone: this.phone,
			fax: this.fax,
			email: this.email,

			privacyPolicy: this.privacyPolicy,
			consentDocument: this.consentDocument,

			storeId: this.storeId,
			refId: this.refId,
		};
	}

	static addFile = async (file) => {
		if (isNotNullOrUndefined(file)) {
			try {
				const fileName = uuid() + '.' + this.getFileExt(file);
				const storageRef = ref(storage, 'coveredEntities/' + fileName);

				return await uploadBytes(storageRef, file);
			} catch (err) {
				console.log(err);
			}
		}

		return null;
	}

	static getFileExt = (file) => {
		const lastDot = file.name.lastIndexOf('.');
		//const fileName = fullFileName.substring(0, lastDot);
		const ext = file.name.substring(lastDot + 1);

		return toLower(ext);
	}

	static uploadImage = async (file) => {
		if (isNotNullOrUndefined(file)) {
			const uploadRes = await this.addFile(file);
			// console.log(uploadRes);
			if (isObjectNotEmpty(uploadRes)) {
				return uploadRes.ref.fullPath;
			}
		}

		return null;
	}

	create = async () => {
		if (isNotNullOrUndefined(this.#image)) {
			this.imageRefId = await CoveredEntity.uploadImage(this.#image);
		}

		return await create(CoveredEntity.collectionName, this.toDto());
	}

	static updateField = async (id, fieldName, newValue) => {
		if (isStringNotEmpty(id)) {
			return await updateField(CoveredEntity.collectionName, id, fieldName, newValue);
		} else {
			throw new Error("id is required.");
		}
	}

	static updateFields = async (id, updatedFields) => {
		if (isStringNotEmpty(id)) {
			return await updateFields(CoveredEntity.collectionName, id, updatedFields);
		} else {
			throw new Error("id is required.");
		}
	}

	static update = async (id, updatedCoveredEntity, oldCoveredEntity) => {
		const updatedObj = new CoveredEntity(updatedCoveredEntity);
		if (id === updatedCoveredEntity.id) {
			if (isNotNullOrUndefined(updatedObj.#image)) {
				updatedObj.imageRefId = await this.uploadImage(updatedObj.#image);
			}

			const success = await update(CoveredEntity.collectionName, id, updatedObj.toDto());
			if (isBooleanTrue(success)) {
				return { ...oldCoveredEntity, ...updatedObj.toDto() };
			} else {
				throw new Error('Failed to update covered entity.');
			}
		} else {
			throw new Error('id and id field of the updated covered entity must match.')
		}

		return null;
	}

	static delete = async (id) => {
		if (isStringNotEmpty(id)) {
			return await remove(CoveredEntity.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static getById = async (id) => {
		if (isStringNotEmpty(id)) {
			return await getById(CoveredEntity.collectionName, id);
		} else {
			throw new Error("id is required.");
		}
	}

	static getByIds = async (ids) => {
		return await getByIdQuery(CoveredEntity.collectionName, ids, 'in');
	}

	static getAll = async () => {
		return await getAll(CoveredEntity.collectionName);
	}

	static getByField = async (fieldName, fieldValue, comparer = '==') => {
		return await getBySingleQuery(CoveredEntity.collectionName, fieldName, fieldValue, comparer);
	}

	static getCount = async (queryParameters) => {
		const { state, postalCode, coveredEntityType } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(coveredEntityType)) {
			queryConstraints.push(where('coveredEntityType', 'in', coveredEntityType));
		} else if (isStringNotEmptyOrZeroLength(coveredEntityType)) {
			queryConstraints.push(where('coveredEntityType', '==', coveredEntityType));
		}

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		if (isListNotEmpty(postalCode)) {
			queryConstraints.push(where('shippingAddress.postalCode', 'in', postalCode));
		} else if (isStringNotEmptyOrZeroLength(postalCode)) {
			queryConstraints.push(where('shippingAddress.postalCode', '==', postalCode));
		}

		let q = query(collection(db, CoveredEntity.collectionName), ...queryConstraints);

		const snapShot = await getCountFromServer(q);

		//console.log(snapShot.data());

		return snapShot.data().count;
	}

	static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible) => {
		const { state, postalCode, coveredEntityType } = queryParameters;

		let queryConstraints = [];

		if (isListNotEmpty(coveredEntityType)) {
			queryConstraints.push(where('coveredEntityType', 'in', coveredEntityType));
		} else if (isStringNotEmptyOrZeroLength(coveredEntityType)) {
			queryConstraints.push(where('coveredEntityType', '==', coveredEntityType));
		}

		if (isListNotEmpty(state)) {
			queryConstraints.push(where('shippingAddress.state', 'in', state));
		} else if (isStringNotEmptyOrZeroLength(state)) {
			queryConstraints.push(where('shippingAddress.state', '==', state));
		}

		if (isListNotEmpty(postalCode)) {
			queryConstraints.push(where('shippingAddress.postalCode', 'in', postalCode));
		} else if (isStringNotEmptyOrZeroLength(postalCode)) {
			queryConstraints.push(where('shippingAddress.postalCode', '==', postalCode));
		}

		let q = null;
		if (isObjectNotEmpty(firstVisible)) {
			q = query(collection(db, CoveredEntity.collectionName), ...queryConstraints, orderBy('name', 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible['name'], firstVisible['id']));
		} else if (isObjectNotEmpty(lastVisible)) {
			q = query(collection(db, CoveredEntity.collectionName), ...queryConstraints, orderBy('name', 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible['name'], lastVisible['id']));
		} else {
			q = query(collection(db, CoveredEntity.collectionName), ...queryConstraints, orderBy('name', 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
		}

		let snapshot = await getDocs(q);
		return snapshot.docs.map((doc) => {
			return {
				id: doc.id,
				...doc.data()
			};
		});
	}

	static get = async (queryParameters) => {
		let allRecords = [];
		try {
			let next = null;

			let totalCount = await CoveredEntity.getCount(queryParameters);
			let totalPages = Math.ceil(totalCount / 10000.0);
			// console.log('totalCount: ' + totalCount);
			// console.log('totalPages: ' + totalPages);
			let pages = [];
			for (let i = 0; i < totalPages; i++) {
				pages.push(i + 1);
			}
			for (const page of pages) {
				// console.log('starting page ' + page);

				let list = await CoveredEntity.getPaginated(queryParameters, 10000, null, next);

				// console.log(list.length);
				allRecords = [...allRecords, ...list];

				// console.log(allRecords.length);

				// console.log('go to next page');
				next = list[list.length - 1];
			}
		} catch (error) {
			console.log(error);
		}

		return allRecords;
	}
}

export default CoveredEntity;